import { useEffect, useState } from 'react';
import { KeyRound, AlertTriangle } from 'lucide-react';
import { mfaChallenge, getDisplayErrorMessage, isRateLimited } from '@/lib/api';
import { getMfaFlow, clearMfaFlow, landingPathFor } from '@/lib/mfaFlow';
import { takeReturnPath } from '@/lib/returnPath';
import { useAuthStore } from '@/store/auth';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/AuthForm.module.css';
import styles from './MfaChallengePage.module.css';
import type { LoginResponse } from '@evi/shared';

interface Props {
  onNavigate: (path: string) => void;
}

/** Backup stashes at or under this count trigger the regenerate
 *  warning interstitial after a backup-code sign-in. */
const LOW_BACKUP_CODES = 3;

/**
 * MFA challenge screen (commit 4 of the admin-MFA arc). Reached
 * when login or magic-verify returns the MFA-pending shape with
 * variant 'challenge' — dormant until commit 5 arms the server.
 *
 * One input, two modes: the 6-digit TOTP (default) or, via the
 * toggle, an XXXXX-XXXXX backup code. Both exchange through
 * POST /auth/mfa/challenge. A backup-code win whose
 * backup_codes_remaining runs low pauses on a gentle warning
 * interstitial before entering the app; the session is already
 * stored at that point, the pause is purely informational.
 */
export function MfaChallengePage({ onNavigate }: Props) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const flow = getMfaFlow();

  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Low-stash interstitial state: session stored, navigation held
  // until the admin acknowledges the warning.
  const [lowCodes, setLowCodes] = useState<{ remaining: number; landing: string } | null>(null);

  useEffect(() => {
    if (!flow || flow.variant !== 'challenge') onNavigate('/login');
  }, [flow, onNavigate]);

  if (!flow || flow.variant !== 'challenge') return null;

  const switchMode = (next: 'totp' | 'backup') => {
    setMode(next);
    setCode('');
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (submitting || trimmed.length < 6) return;
    setSubmitting(true);
    setError(null);
    try {
      const res: LoginResponse & { backup_codes_remaining?: number } =
        await mfaChallenge(flow.pendingToken, trimmed);
      setAuth(res.user, res.access_token);
      clearMfaFlow();
      const landing = takeReturnPath() ?? landingPathFor(res.user);
      if (
        typeof res.backup_codes_remaining === 'number'
        && res.backup_codes_remaining <= LOW_BACKUP_CODES
      ) {
        setLowCodes({ remaining: res.backup_codes_remaining, landing });
        return;
      }
      onNavigate(landing);
    } catch (err) {
      if (isRateLimited(err)) {
        setError('Too many attempts. Wait a moment, then try again.');
      } else {
        setError(await getDisplayErrorMessage(err, 'Invalid verification code'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Low-backup-codes interstitial ──────────────────────────────────
  if (lowCodes) {
    return (
      <AuthLayout
        title="You're signed in"
        subtitle="A quick heads-up about your backup codes"
      >
        <div className={styles.stack}>
          <div className={styles.warnBanner} role="alert">
            <AlertTriangle size={16} aria-hidden />
            {lowCodes.remaining === 0
              ? 'That was your last backup code. Losing your authenticator now would lock you out.'
              : `Only ${lowCodes.remaining} backup ${lowCodes.remaining === 1 ? 'code' : 'codes'} left.`}
            {' '}Ask a super admin to reset your MFA so you can re-enroll with fresh codes.
          </div>
          <button
            type="button"
            className={formStyles.submitBtn}
            onClick={() => onNavigate(lowCodes.landing)}
          >
            Continue
          </button>
        </div>
      </AuthLayout>
    );
  }

  const isTotp = mode === 'totp';
  return (
    <AuthLayout
      title="Two-factor authentication"
      subtitle={isTotp
        ? 'Enter the 6-digit code from your authenticator app'
        : 'Enter one of your one-time backup codes'}
    >
      <div className={styles.stack}>
        <form onSubmit={(e) => { void onSubmit(e); }} noValidate className={formStyles.form}>
          <div className={formStyles.field}>
            <label htmlFor="mfa-challenge-code" className={formStyles.label}>
              {isTotp ? 'Verification code' : 'Backup code'}
            </label>
            <input
              id="mfa-challenge-code"
              type="text"
              inputMode={isTotp ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              maxLength={isTotp ? 6 : 12}
              className={`${formStyles.input} ${isTotp ? styles.totpInput : styles.backupInput} ${error ? formStyles.inputError : ''}`}
              placeholder={isTotp ? '123456' : 'XXXXX-XXXXX'}
              value={code}
              onChange={(e) => setCode(
                isTotp
                  ? e.target.value.replace(/\D/g, '')
                  : e.target.value.toUpperCase(),
              )}
              autoFocus
            />
            {error && <p className={formStyles.error}>{error}</p>}
          </div>
          <button
            type="submit"
            className={formStyles.submitBtn}
            disabled={submitting || code.trim().length < 6}
          >
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
        </form>

        <button
          type="button"
          className={styles.modeToggle}
          onClick={() => switchMode(isTotp ? 'backup' : 'totp')}
        >
          <KeyRound size={14} aria-hidden />
          {isTotp ? 'Use a backup code instead' : 'Use your authenticator app instead'}
        </button>
      </div>
    </AuthLayout>
  );
}
